import { useState } from 'react';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Search, 
  Filter,
  UserPlus,
  DollarSign,
  User,
  Briefcase,
  FileText,
  MessageSquare,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';

const entityConfig = {
  lead: { label: 'Lead', color: 'blue', icon: UserPlus },
  deal: { label: 'Negócio', color: 'green', icon: DollarSign },
  contact: { label: 'Contato', color: 'purple', icon: User },
  case: { label: 'Processo', color: 'orange', icon: Briefcase },
  document: { label: 'Documento', color: 'red', icon: FileText },
  message: { label: 'Mensagem', color: 'cyan', icon: MessageSquare }
};

const actionConfig = {
  create: { label: 'Criado', variant: 'default' },
  update: { label: 'Atualizado', variant: 'secondary' },
  delete: { label: 'Excluído', variant: 'destructive' },
  status_change: { label: 'Status Alterado', variant: 'default' },
  assign: { label: 'Atribuído', variant: 'default' },
  approve: { label: 'Aprovado', variant: 'default' },
  reject: { label: 'Recusado', variant: 'destructive' }
};

export default function AuditLogs() {
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const limit = 50;

  const { logs, loading, totalCount } = useAuditLogs({ ...filters, search: searchTerm });

  const totalPages = Math.ceil(totalCount / limit);

  const EntityBadge = ({ type }) => {
    const config = entityConfig[type] || { label: type, color: 'gray', icon: FileText };
    const Icon = config.icon;
    return (
      <Badge variant="outline" className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const ActionBadge = ({ action }) => {
    const config = actionConfig[action] || { label: action, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Logs de Auditoria</h1>
        <p className="text-muted-foreground">
          Acompanhe todas as alterações feitas em leads, negócios, contatos e mais
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome da entidade ou usuário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select
                value={filters.entityType || 'all'}
                onValueChange={(value) => 
                  setFilters({ ...filters, entityType: value === 'all' ? undefined : value })
                }
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Entidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Entidades</SelectItem>
                  <SelectItem value="lead">Leads</SelectItem>
                  <SelectItem value="deal">Negócios</SelectItem>
                  <SelectItem value="contact">Contatos</SelectItem>
                  <SelectItem value="case">Processos</SelectItem>
                  <SelectItem value="document">Documentos</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.actionType || 'all'}
                onValueChange={(value) => 
                  setFilters({ ...filters, actionType: value === 'all' ? undefined : value })
                }
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Ação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Ações</SelectItem>
                  <SelectItem value="create">Criar</SelectItem>
                  <SelectItem value="update">Atualizar</SelectItem>
                  <SelectItem value="delete">Excluir</SelectItem>
                  <SelectItem value="status_change">Mudança de Status</SelectItem>
                  <SelectItem value="assign">Atribuir</SelectItem>
                  <SelectItem value="approve">Aprovar</SelectItem>
                  <SelectItem value="reject">Recusar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum log de auditoria encontrado
            </div>
          ) : (
            <>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Entidade</TableHead>
                      <TableHead>Ação</TableHead>
                      <TableHead>Campo</TableHead>
                      <TableHead>Valor Anterior</TableHead>
                      <TableHead>Novo Valor</TableHead>
                      <TableHead>Usuário</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-xs">
                          {safeFormat(new Date(log.created_at), 'yyyy-MM-dd HH:mm:ss')}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <EntityBadge type={log.entity_type} />
                            <span className="text-sm text-muted-foreground">{log.entity_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ActionBadge action={log.action_type} />
                        </TableCell>
                        <TableCell className="text-sm">{log.field_changed || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                          {log.old_value || '-'}
                        </TableCell>
                        <TableCell className="text-sm font-medium max-w-[150px] truncate">
                          {log.new_value || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{log.changed_by_name || 'Sistema'}</span>
                            <span className="text-xs text-muted-foreground">{log.user_email}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedLog(log)}
                          >
                            Detalhes
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Mostrando {page * limit + 1} a {Math.min((page + 1) * limit, totalCount)} de {totalCount} logs
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      Próximo
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Details Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Log de Auditoria</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Entidade</p>
                  <div className="flex items-center gap-2 mt-1">
                    <EntityBadge type={selectedLog.entity_type} />
                    <span className="text-sm">{selectedLog.entity_name}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Ação</p>
                  <div className="mt-1">
                    <ActionBadge action={selectedLog.action_type} />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Usuário</p>
                  <p className="text-sm mt-1">{selectedLog.changed_by_name || 'Sistema'}</p>
                  <p className="text-xs text-muted-foreground">{selectedLog.user_email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Data/Hora</p>
                  <p className="text-sm mt-1 font-mono">
                    {safeFormat(new Date(selectedLog.created_at), 'PPpp')}
                  </p>
                </div>
              </div>

              {selectedLog.field_changed && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Campo Alterado</p>
                  <Badge variant="outline">{selectedLog.field_changed}</Badge>
                </div>
              )}

              {(selectedLog.old_value || selectedLog.new_value) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Valor Anterior</p>
                    <div className="p-3 bg-muted rounded-lg">
                      <pre className="text-sm whitespace-pre-wrap break-words">
                        {selectedLog.old_value || '-'}
                      </pre>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Novo Valor</p>
                    <div className="p-3 bg-muted rounded-lg">
                      <pre className="text-sm whitespace-pre-wrap break-words">
                        {selectedLog.new_value || '-'}
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Metadados</p>
                  <div className="p-3 bg-muted rounded-lg">
                    <pre className="text-sm whitespace-pre-wrap break-words">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
